/**
 * Shared inventory mechanics: which account a location maps to, and FEFO batch consumption.
 *
 * Lives outside the reducers because sales, write-offs, transfers and repackaging all move
 * stock and all have to draw down batches the same way. One implementation means a sale and a
 * write-off can never disagree about which batch went out.
 */

import {
  ZERO_QTY,
  addQty,
  centavos,
  costOf,
  esCamion,
  milli,
  subMoney,
  subQty,
  unitCostFromTotal,
  type AnyDomainEvent,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import type { AccountKey } from './ledger/accounts.js';
import { credit, debit, entry, type JournalLine } from './ledger/journal.js';
import type { Effect, Existencia, Lote, Reader, SesionCaja, Venta } from './state.js';

/** Narrowed so callers can read back the row they just produced without re-narrowing. */
export type EfectoLote = Extract<Effect, { kind: 'lote.upsert' }>;

/**
 * Stock in a truck is not stock on the shelf, and the balance sheet should say so. A shop-floor
 * to storeroom move maps to the same account and therefore posts nothing.
 */
export function cuentaInventario(ubicacion: string): AccountKey {
  if (ubicacion.startsWith('camion:')) return 'INVENTARIO_CAMION';
  if (ubicacion === 'transformacion') return 'INVENTARIO_TRANSFORMACION';
  return 'INVENTARIO';
}

/**
 * Where what comes back of a sale goes: where it was sold from — except off a truck whose route
 * was settled since (its till closed). The settlement emptied the truck into the shop
 * (`LiquidarRuta`), and what comes back after comes back there too, or to the till that hands its
 * money back. Put back on the empty truck, it waited there for the next route's settlement, which
 * found it missing and charged it to that driver (2026-10-13).
 */
export function adondeVuelve(
  venta: Pick<Venta, 'ubicacion'>,
  suCaja: Pick<SesionCaja, 'estado'> | undefined,
  cajaQueDevuelve: Pick<SesionCaja, 'ubicacion'> | undefined,
): string {
  if (!esCamion(venta.ubicacion) || suCaja?.estado === 'abierta') return venta.ubicacion;
  return cajaQueDevuelve?.ubicacion ?? 'tienda';
}

/**
 * When something happened, on the server's clock: the device's time, less how far off its clock
 * was last measured to be. Two devices' times only compare this way.
 */
export function momentoDe(event: AnyDomainEvent, ts: string = event.clientTs): number {
  return Date.parse(ts) - (event.clockOffsetMs ?? 0);
}

/**
 * Whether a count already took this movement in.
 *
 * It happened before the stock was last counted but reached the books after it — a phone that
 * sold with no signal syncs once the count is in. The shelf at the count already showed it, so
 * moving the stock again would count it twice. The stock stays as the count left it, and the
 * cost goes against «Faltante de inventario» instead: what the count booked as missing (or over)
 * turns out to have been this movement.
 */
export function yaContado(actual: Existencia | undefined, event: AnyDomainEvent): boolean {
  return actual?.contadoEn != null && momentoDe(event) < Date.parse(actual.contadoEn);
}

/**
 * Goods that came in before a count that reached the books first (`yaContado`), worth `valor`.
 *
 * The count already has them, at what stood there was worth a unit — which may be nothing: a
 * storeroom never stocked with them, bags never made before. So the quantity stays as counted and
 * what they were worth goes into what the stock is worth (`aEstante`, exactly what the row's value
 * changes by); the rest takes back what the count booked as over (`aFaltante`, against «Faltante
 * de inventario» as a returned sale's does). Counted at nothing or less, all of it goes there.
 */
export function entradaYaContada(
  actual: Existencia,
  cantidad: Milli,
  valor: Centavos,
  movimientoId: string,
  rev: number,
): { efecto: Effect | null; aEstante: Centavos; aFaltante: Centavos } {
  if (actual.cantidad <= 0) return { efecto: null, aEstante: centavos(0), aFaltante: valor };
  const antes = costOf(actual.costoPromedio, actual.cantidad);
  const despues = centavos(Math.max(0, antes + valor - costOf(actual.costoPromedio, cantidad)));
  const costoPromedio = unitCostFromTotal(despues, actual.cantidad);
  const aEstante = subMoney(costOf(costoPromedio, actual.cantidad), antes);
  return {
    efecto: { kind: 'existencia.set', id: actual.id, row: { ...actual, costoPromedio, ultimoMovimientoId: movimientoId, rev } },
    aEstante,
    aFaltante: subMoney(valor, aEstante),
  };
}

/**
 * Draw a quantity out of batches, earliest expiry first.
 *
 * FEFO rather than FIFO: what matters for medicine and perishables is what expires soonest, not
 * what arrived first — and a batch bought later can easily expire sooner.
 *
 * Products with no batches consume nothing and that is fine: batches exist only for items
 * flagged as medicine or perishable, so most of the catalog never touches this.
 *
 * If the batches do not cover the quantity, the earliest one absorbs the rest and goes negative.
 * Same principle as negative stock: never block the movement, make the gap visible.
 */
export function consumirLotesFefo(
  lotes: readonly Lote[],
  cantidad: Milli,
  rev: number,
): EfectoLote[] {
  // A tie of expiry by the batch's id, letter by letter: by the language, «ÑA-1» came before «NA-2»
  // on a phone in Spanish and after it on the server (2026-10-20).
  const conSaldo = lotes
    .filter((l) => l.cantidadDisponible > 0)
    .sort((a, b) => a.vencimiento.localeCompare(b.vencimiento) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  if (conSaldo.length === 0) return [];

  const effects: EfectoLote[] = [];
  let restante = cantidad;

  for (const lote of conSaldo) {
    if (restante <= 0) break;
    const toma = milli(Math.min(lote.cantidadDisponible, restante));
    effects.push({
      kind: 'lote.upsert',
      id: lote.id,
      row: { ...lote, cantidadDisponible: subQty(lote.cantidadDisponible, toma), rev },
    });
    restante = subQty(restante, toma);
  }

  if (restante > 0) {
    const primero = conSaldo[0]!;
    const fila = effects[0]!.row;
    effects[0] = {
      kind: 'lote.upsert',
      id: primero.id,
      row: { ...fila, cantidadDisponible: subQty(fila.cantidadDisponible, restante), rev },
    };
  }

  return effects;
}

/** Puts quantity back into batches, newest-expiry last — the mirror of a FEFO draw. */
export function devolverLotes(lotes: readonly Lote[], cantidad: Milli, rev: number): EfectoLote[] {
  const ordenados = [...lotes].sort(
    (a, b) => b.vencimiento.localeCompare(a.vencimiento) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0),
  );
  if (ordenados.length === 0) return [];

  const effects: EfectoLote[] = [];
  let restante = cantidad;

  for (const lote of ordenados) {
    if (restante <= 0) break;
    const hueco = subQty(lote.cantidadRecibida, lote.cantidadDisponible);
    if (hueco <= 0) continue;
    const pone = milli(Math.min(hueco, restante));
    effects.push({
      kind: 'lote.upsert',
      id: lote.id,
      row: { ...lote, cantidadDisponible: addQty(lote.cantidadDisponible, pone), rev },
    });
    restante = subQty(restante, pone);
  }

  return effects;
}

export async function lotesConSaldo(
  read: Reader,
  productoId: string,
  ubicacion: string,
): Promise<Lote[]> {
  const lotes = await read.lotesDe(productoId, ubicacion);
  return lotes.filter((l) => l.cantidadDisponible !== ZERO_QTY);
}

/** Goods an event put back on one stock line: the line before and after, and what it booked in. */
export interface Recibido {
  antes: { cantidad: Milli; costoPromedio: UnitCost };
  despues: { cantidad: Milli; costoPromedio: UnitCost };
  /** What the event booked into the stock account for these goods. */
  entrante: Centavos;
  cuenta: AccountKey;
}

/**
 * Goods coming in to stock that was below zero: sold before they arrived, costed at the average of
 * the day, which may not be what they cost. They are those units, and the average starts again
 * from what they cost (`weightedAverageCost`), so the shelf's value moves by more or less than
 * what came in. The gap is what those sales really cost beyond what was booked. It goes to cost of
 * sales, so stock on the books stays the shelf at its average (`reportes/cuadres.ts`); before, it
 * went nowhere, and the two drifted apart by it every time.
 *
 * `deshacer` books the same gap back out: a purchase annulled restores the line as it was before.
 */
export function ajusteVendidoAntes(
  eventId: string,
  fecha: string,
  recibidos: readonly Recibido[],
  deshacer = false,
): Effect | null {
  const lineas: JournalLine[] = [];
  for (const r of recibidos) {
    if (r.antes.cantidad >= 0) continue;
    const brecha =
      costOf(r.despues.costoPromedio, r.despues.cantidad) - costOf(r.antes.costoPromedio, r.antes.cantidad) - r.entrante;
    const sube = deshacer ? brecha < 0 : brecha > 0;
    const monto = centavos(Math.abs(brecha));
    if (monto === 0) continue;
    lineas.push(
      ...(sube
        ? [debit(r.cuenta, monto), credit('COSTO_VENTAS', monto, 'Vendido antes de que llegara')]
        : [debit('COSTO_VENTAS', monto, 'Vendido antes de que llegara'), credit(r.cuenta, monto)]),
    );
  }
  if (lineas.length === 0) return null;
  return { kind: 'asiento', entry: entry(eventId, fecha, 'Costo de lo vendido antes de que llegara', lineas) };
}
